/**
 * 实体模型：schema 常量 + 工厂函数 + 校验
 * ------------------------------------------------------------
 * 所有写入存储的数据都必须先经过这里的工厂函数，保证字段一致。
 * 新增内容类型：在这里加 makeXxx + validateXxx，并在 core/schema.js 加集合。
 */
import { uid, nowISO, hash } from '../utils/index.js';

export const CONFIRMED = 'confirmed';
export const PENDING = 'pending';

/** 题型注册表（渲染/判分/生成各自扩展，这里只做元数据） */
export const QUESTION_TYPES = {
  choice: { label: '单项选择', defaultSkill: 'G' },
  fill: { label: '填空', defaultSkill: 'G' },
  judge: { label: '判断', defaultSkill: 'G' },
  correct: { label: '改错', defaultSkill: 'G' },
  transform: { label: '句子转换', defaultSkill: 'G' },
  order: { label: '单词排序', defaultSkill: 'S' },
  spell: { label: '拼写', defaultSkill: 'W' },
  listen: { label: '听音选择', defaultSkill: 'W' },
};

/** 技能维度：W 单词 / S 句子 / G 语法 / R 记忆提取 */
export const SKILLS = { W: '单词', S: '句子', G: '语法', R: '记忆提取' };

/* ------------------------------ 内容域 ------------------------------ */

export const makeTextbook = (d = {}) => ({
  id: d.id || uid('tb'),
  name: d.name || '未命名教材',
  grade: Number(d.grade) || 0,
  term: d.term || '上',
  version: d.version || 'PEP',
  publisher: d.publisher || '',
  note: d.note || '',
  order: Number(d.order) || 0,
  createdAt: d.createdAt || nowISO(),
});

export const makeUnit = (d = {}) => ({
  id: d.id || uid('u'),
  textbookId: d.textbookId,
  no: Number(d.no) || 1,
  title: d.title || '未命名单元',
  theme: d.theme || '',
  objectives: Array.isArray(d.objectives) ? d.objectives : [],
  sourcePages: Array.isArray(d.sourcePages) ? d.sourcePages : [],
  createdAt: d.createdAt || nowISO(),
});

export const makeWord = (d = {}) => ({
  id: d.id || uid('w'),
  textbookId: d.textbookId || '',
  unitId: d.unitId || '',
  en: String(d.en || '').trim(),
  zh: String(d.zh || '').trim(),
  phonetic: d.phonetic || '',
  pos: d.pos || '',
  phrase: d.phrase || '',
  phraseZh: d.phraseZh || '',
  sentence: d.sentence || '',
  sentenceZh: d.sentenceZh || '',
  source: d.source || '',
  page: d.page ?? null,
  status: d.status || CONFIRMED,
  needsReview: !!d.needsReview,
  confidence: d.confidence ?? 1,
  createdAt: d.createdAt || nowISO(),
});

export const makeSentence = (d = {}) => ({
  id: d.id || uid('s'),
  textbookId: d.textbookId || '',
  unitId: d.unitId || '',
  en: String(d.en || '').trim(),
  zh: String(d.zh || '').trim(),
  pattern: d.pattern || '',
  keyPattern: !!d.keyPattern,
  page: d.page ?? null,
  source: d.source || '',
  status: d.status || CONFIRMED,
  needsReview: !!d.needsReview,
  confidence: d.confidence ?? 1,
  createdAt: d.createdAt || nowISO(),
});

export const makeGrammar = (d = {}) => ({
  id: d.id || uid('g'),
  textbookId: d.textbookId || '',
  unitId: d.unitId || '',
  category: d.category || '其他',
  rule: d.rule || '',
  examples: Array.isArray(d.examples) ? d.examples : [],
  page: d.page ?? null,
  source: d.source || '',
  status: d.status || CONFIRMED,
  needsReview: !!d.needsReview,
  confidence: d.confidence ?? 1,
  createdAt: d.createdAt || nowISO(),
});

export const makeQuestion = (d = {}) => ({
  id: d.id || uid('q'),
  textbookId: d.textbookId || '',
  unitId: d.unitId || '',
  type: QUESTION_TYPES[d.type] ? d.type : 'choice',
  skill: d.skill || QUESTION_TYPES[d.type]?.defaultSkill || 'G',
  level: Number(d.level) || 1,
  prompt: d.prompt || '',
  options: Array.isArray(d.options) ? d.options : [],
  answer: Array.isArray(d.answer) ? d.answer : (d.answer ? [String(d.answer)] : []),
  accept: Array.isArray(d.accept) ? d.accept : [],
  explain: d.explain || '',
  hint: d.hint || '',
  // 音频：audioUrl = 内置 MP3 相对路径（如 "audio/u2/strong.mp3"）；
  //       audio = 英文文本（MP3 缺失/播放失败时走 TTS 的兜底朗读内容）
  audioUrl: d.audioUrl || '',
  audio: d.audio || '',
  refType: d.refType || '',        // word | sentence | grammar
  refId: d.refId || '',
  grammarCategory: d.grammarCategory || '',
  origin: d.origin || 'generated', // textbook | generated
  sourceFile: d.sourceFile || '',
  page: d.page ?? null,
  status: d.status || CONFIRMED,
  needsReview: !!d.needsReview,
  confidence: d.confidence ?? 1,
  contentHash: d.contentHash || '',
  createdAt: d.createdAt || nowISO(),
});

/* ------------------------------ 学习域 ------------------------------ */

export const makeStudent = (d = {}) => ({
  id: d.id || uid('stu'),
  name: d.name || '同学',
  avatar: d.avatar || '🦊',
  xp: Number(d.xp) || 0,
  createdAt: d.createdAt || nowISO(),
});

export const makeAchievement = (d = {}) => ({
  id: d.id || uid('ach'),
  studentId: d.studentId,
  code: d.code,
  unitId: d.unitId || '',
  unlockedAt: d.unlockedAt || nowISO(),
});

/* ------------------------------ 校验 ------------------------------ */

export function validateTextbook(tb) {
  const errors = [];
  if (!tb.name) errors.push('教材名称不能为空');
  if (!tb.grade) errors.push('年级不能为空');
  return errors;
}

export function validateUnit(u) {
  const errors = [];
  if (!u.textbookId) errors.push('缺少所属教材');
  if (!u.title) errors.push('单元标题不能为空');
  return errors;
}

export function validateWord(w) {
  const errors = [];
  if (!w.unitId) errors.push('缺少所属单元');
  if (!w.en) errors.push('英文单词不能为空');
  if (!w.zh) errors.push('中文意思为空（将标记待确认）');
  return errors;
}

export function validateQuestion(q) {
  const errors = [];
  if (!q.unitId) errors.push('缺少所属单元');
  if (!q.prompt) errors.push('题干不能为空');
  if (!q.answer.length) errors.push('缺少正确答案');
  if (q.type === 'choice' && q.options.length < 2) errors.push('选择题至少需要 2 个选项');
  return errors;
}

/* ------------------------------ 去重键 ------------------------------ */

export const wordKey = w => `${w.unitId}::${String(w.en).toLowerCase().trim()}`;
export const sentenceKey = s => `${s.unitId}::${String(s.en).toLowerCase().replace(/\s+/g, ' ').trim()}`;
export const questionKey = q => hash(`${q.unitId}|${q.type}|${q.prompt}`);

export function contentHashOf(kind, obj) {
  if (kind === 'word') return wordKey(obj);
  if (kind === 'sentence') return sentenceKey(obj);
  if (kind === 'question') return questionKey(obj);
  return hash(`${kind}|${obj.unitId}|${obj.category || ''}|${obj.rule || ''}`);
}
